import React, { useEffect, useState } from "react"
import { formatDateDataGrid } from "src/utils/format-date"
import PropTypes from "prop-types"
import ParameterPaginationTable from "src/components/table/parameter-pagination-table"
import DatalakeService from "src/services/datalake-service"
import dayjs from "dayjs"
import { DATE_FORMAT } from "src/utils/constants"

const EDITABLE_FIELDS = [
    "CETIP",
    "CPFCNPJ",
    "DataReferencia",
    "IDRiskFree",
    "IDCurvaPRE",
    "QtNegociada",
    "Indicador",
    "PreviaInflacao",
    "DataUltimoRating",
    "DataRatingVigente",
    "StatusRating",
    "AliquotaIR",
    "FatorIR",
    "TaxaAccrual",
    "DurationAccrual",
    "PU_MTM",
    "TaxaMTM",
    "PUPAR",
    "PercentualPUPAR",
    "DurationMTM",
    "TaxaRiskFree",
    "TaxaCurvaRating",
    "TIR_ComIR",
    "TIR_SemIR",
    "TIR_Original",
    "SpreadCurvaRating",
    "SpreadCreditoComIR",
    "SpreadCreditoSemIR",
    "SpreadCreditoFinal",
    "SpreadRelativoComIR",
    "SpreadRelativoSemIR",
    "SpreadRelativoFinal",
    "Fonte",
    "LOGERRO",
]

const DATE_FIELDS = new Set(["DataReferencia", "DataUltimoRating", "DataRatingVigente"])
const NUMBER_FIELDS = new Set([
    "IDRiskFree",
    "IDCurvaPRE",
    "QtNegociada",
    "PreviaInflacao",
    "AliquotaIR",
    "FatorIR",
    "TaxaAccrual",
    "DurationAccrual",
    "PU_MTM",
    "TaxaMTM",
    "PUPAR",
    "PercentualPUPAR",
    "DurationMTM",
    "TaxaRiskFree",
    "TaxaCurvaRating",
    "TIR_ComIR",
    "TIR_SemIR",
    "TIR_Original",
    "SpreadCurvaRating",
    "SpreadCreditoComIR",
    "SpreadCreditoSemIR",
    "SpreadCreditoFinal",
    "SpreadRelativoComIR",
    "SpreadRelativoSemIR",
    "SpreadRelativoFinal",
])

const getDefaultRow = () => ({
    CETIP: "N/A",
    CPFCNPJ: null,
    DataReferencia: dayjs().format(DATE_FORMAT),
    IDRiskFree: 0,
    IDCurvaPRE: null,
    QtNegociada: 0,
    Indicador: "N/A",
    PreviaInflacao: 0,
    DataUltimoRating: null,
    DataRatingVigente: null,
    StatusRating: null,
    AliquotaIR: 0,
    FatorIR: 0,
    TaxaAccrual: 0,
    DurationAccrual: 0,
    PU_MTM: 0,
    TaxaMTM: 0,
    PUPAR: 0,
    PercentualPUPAR: 0,
    DurationMTM: 0,
    TaxaRiskFree: 0,
    TaxaCurvaRating: 0,
    TIR_ComIR: 0,
    TIR_SemIR: 0,
    TIR_Original: 0,
    SpreadCurvaRating: 0,
    SpreadCreditoComIR: 0,
    SpreadCreditoSemIR: 0,
    SpreadCreditoFinal: 0,
    SpreadRelativoComIR: 0,
    SpreadRelativoSemIR: 0,
    SpreadRelativoFinal: 0,
    Fonte: "N/A",
    LOGERRO: null,
})

const formatCPFCNPJ = ({ value }) => value ?? "N/A"

const makeColumn = (field) => ({
    field,
    headerName: field,
    flex: 1,
    minWidth: 150,
    maxWidth: 350,
    disableColumnMenu: true,
    align: "left",
    headerAlign: "left",
    editable: true,
    ...(DATE_FIELDS.has(field) ? { typeComponent: "date" } : {}),
    ...(NUMBER_FIELDS.has(field) ? { typeComponent: "number", type: "number" } : {}),
    ...(field === "CPFCNPJ" ? { valueFormatter: formatCPFCNPJ } : {}),
})

const columns = [
    {
        field: "ID",
        headerName: "#",
        disableColumnMenu: true,
        disableFilter: true,
        flex: true,
        minWidth: 80,
        align: "left",
        headerAlign: "left",
    },
    ...EDITABLE_FIELDS.map(makeColumn),
    {
        field: "CriadoEm",
        headerName: "Data de Criação",
        flex: true,
        minWidth: 150,
        disableColumnMenu: true,
        disableFilter: true,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => formatDateDataGrid(params),
    },
    {
        field: "AtualizadoEm",
        headerName: "Data de Atualização",
        flex: true,
        minWidth: 150,
        disableColumnMenu: true,
        disableFilter: true,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => formatDateDataGrid(params),
    },
]

const getEditablePayload = (row) => {
    const payload = {}
    EDITABLE_FIELDS.forEach((field) => {
        payload[field] = row[field]
    })
    return payload
}

const ParameterDatalakeSpreadCorpIndicativoTable = (props) => {
    const dataLakeService = new DatalakeService()
    const [data, setData] = useState([])
    const [pagination, setPagination] = useState({ count: 0, next: null, previous: null })

    useEffect(() => {
        loadData()
    }, [])

    const loadData = async () => {
        props.loading(true)
        try {
            const response = await dataLakeService.loadPaginationEndpoint(props.endpoint)
            const { results, count, next, previous } = response.body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))
            setPagination({ count, next, previous })
            setData(list)
        } catch (error) {
            props.callbackError(error)
        } finally {
            props.loading(false)
        }
    }

    const createRow = async () => {
        props.loading(true)
        return dataLakeService.createTableEndpoint(props.endpoint, getDefaultRow()).then(() => {
            props.loading(false)
            loadData()
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const updateRow = async (newRow) => {
        const { ID } = newRow
        const body = getEditablePayload(newRow)

        props.loading(true)
        return dataLakeService.updateTableEndpoint(props.endpoint, ID, body).then(() => {
            props.loading(false)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const deleteRow = async (row) => {
        props.loading(true)
        return dataLakeService.deleteTableEndpoint(props.endpoint, row.ID).then(() => {
            props.loading(false)
            setData((currentData) => currentData.filter((item) => item.ID !== row.ID))
            setPagination((currentPagination) => ({
                ...currentPagination,
                count: Math.max(0, currentPagination.count - 1),
            }))
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const onFilter = async (filterList, limit, offset) => {
        const query = filterList.map((obj) => encodeURIComponent(obj.field) + "=" + encodeURIComponent(obj.value)).join("&")

        props.loading(true)
        try {
            const response = await dataLakeService.loadPaginationEndpoint(props.endpoint, query, limit, offset)
            const { results, count, next, previous } = response.body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))
            setPagination({ count, next, previous })
            setData(list)
        } catch (error) {
            props.callbackError(error)
        } finally {
            props.loading(false)
        }
    }

    const changePage = async (endpoint) => {
        props.loading(true)
        try {
            const response = await dataLakeService.paginate(endpoint)
            const { results, count, previous, next } = response.body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))
            setPagination({ count, next, previous })
            setData(list)
        } catch (error) {
            props.callbackError(error)
        } finally {
            props.loading(false)
        }
    }

    return (
        <div>
            <ParameterPaginationTable
                columns={columns}
                data={data}
                setData={setData}
                createRow={createRow}
                deleteRow={deleteRow}
                updateRow={updateRow}
                filterFunction={onFilter}
                pagination={pagination}
                onChangePage={changePage}
                customFilter={true}
            />
        </div>
    )
}

ParameterDatalakeSpreadCorpIndicativoTable.propTypes = {
    loading: PropTypes.func,
    endpoint: PropTypes.string,
    callbackError: PropTypes.func,
}

export default ParameterDatalakeSpreadCorpIndicativoTable
